import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import Box from '@mui/material/Box'
import type { PendingWebsiteTarget, WebsitePreviewBridgeCommandInput, WebsitePreviewBridgeMessage, WebsitePreviewDevice } from './types'
import { injectPreviewBridge, parsePreviewBridgeMessage } from './previewBridge'

export interface WebsitePreviewFrameHandle {
  postCommand: (command: WebsitePreviewBridgeCommandInput) => void
}

export interface WebsitePreviewFrameProps {
  html: string
  siteId: string
  revision: number
  selectMode?: boolean
  readOnly?: boolean
  device?: WebsitePreviewDevice
  selectedTargets?: PendingWebsiteTarget[]
  highlightedTargetIds?: string[]
  title?: string
  onBridgeMessage?: (message: WebsitePreviewBridgeMessage) => void
  onReady?: () => void
}

function nonce(): string {
  const bytes = new Uint8Array(18)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

export const WebsitePreviewFrame = forwardRef<WebsitePreviewFrameHandle, WebsitePreviewFrameProps>(function WebsitePreviewFrame({
  html, siteId, revision, selectMode = false, readOnly = false, device = 'desktop', selectedTargets = [],
  highlightedTargetIds = [], title = 'Site Preview', onBridgeMessage, onReady,
}, forwardedRef) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const frameNonce = useMemo(nonce, [html, siteId, revision])
  const identity = useMemo(() => ({ frameNonce, siteId, revision }), [frameNonce, siteId, revision])
  // Select mode is commanded after load. Keeping it out of srcDoc prevents a full iframe reload
  // whenever the toolbar toggle changes.
  const srcDoc = useMemo(() => injectPreviewBridge(html, identity, false), [html, identity])

  const post = (command: WebsitePreviewBridgeCommandInput) => {
    iframeRef.current?.contentWindow?.postMessage({ ...command, source: 'ros-website-host', version: 1, frameNonce }, '*')
  }
  useImperativeHandle(forwardedRef, () => ({ postCommand: post }))

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      const message = parsePreviewBridgeMessage(event.data, identity)
      if (!message) return
      if (message.type === 'bridge-ready') onReady?.()
      onBridgeMessage?.(message)
    }
    window.addEventListener('message', receive)
    return () => window.removeEventListener('message', receive)
  }, [identity, onBridgeMessage, onReady])

  useEffect(() => { post({ type: 'set-select-mode', enabled: selectMode && !readOnly }) }, [selectMode, readOnly, frameNonce])
  useEffect(() => { post({ type: 'set-selected-targets', targetIds: selectedTargets.map(t => t.targetId) }) }, [selectedTargets, frameNonce])
  useEffect(() => { post({ type: 'set-highlighted-targets', targetIds: highlightedTargetIds }) }, [highlightedTargetIds, frameNonce])

  const syncFrameState = () => {
    post({ type: 'set-select-mode', enabled: selectMode && !readOnly })
    post({ type: 'set-selected-targets', targetIds: selectedTargets.map(target => target.targetId) })
    if (highlightedTargetIds.length) post({ type: 'set-highlighted-targets', targetIds: highlightedTargetIds })
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', display: 'flex', justifyContent: 'center', bgcolor: device === 'mobile' ? 'action.hover' : 'transparent', py: device === 'mobile' ? 2 : 0 }}>
      <Box
        component="iframe"
        ref={iframeRef}
        srcDoc={srcDoc}
        onLoad={syncFrameState}
        sandbox="allow-scripts"
        title={title}
        sx={device === 'mobile' ? {
          width: 375, height: '100%', maxHeight: 812, flexShrink: 0,
          border: '6px solid', borderColor: 'text.secondary', borderRadius: 4, bgcolor: 'background.paper',
        } : { width: '100%', height: '100%', border: 0, bgcolor: 'background.paper' }}
      />
    </Box>
  )
})
